import type { Dispatch } from 'react';
import { useEffect, useRef } from 'react';
import type { BrandInsightsInstanceContext, TagFilter } from '@trinity/chat';
import { getChatContextMetadata, getSegmentFromChatContext } from '@trinity/chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import type { ChatState } from './interfaces';

type Params = {
  pageContext: BrandInsightsInstanceContext.ItemWithMetadata | undefined;
  chatIdentifier: string;
  tags: TagFilter.Item[] | undefined;
  dispatch: Dispatch<ChatState.Action>;
};

/**
 * Navigating to a segment puts it in the page context (`metadata.segment`). Mirror it into the Tags
 * filter as a locked (non-removable) segment tag, the same shape `SegmentChatSelection` applies when
 * a segment is picked inside the chat. Re-applies when the segment or the chat changes (a new chat
 * starts with empty filters) and drops only the tag it applied when the segment goes away.
 */
export function useSegmentTagFilterSync({ pageContext, chatIdentifier, tags, dispatch }: Params) {
  const apiClient = useChatApiClient();
  const segment = pageContext ? getSegmentFromChatContext(pageContext) : undefined;
  const segmentId = segment?.id;
  const segmentName = segment?.name;

  const tagsRef = useRef(tags);
  tagsRef.current = tags;
  const appliedTagIdRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!pageContext || !segmentId || !segmentName) {
      const appliedTagId = appliedTagIdRef.current;
      if (appliedTagId !== null) {
        appliedTagIdRef.current = null;
        dispatch({
          type: 'filters/updated',
          payload: { filters: { tags: (tagsRef.current ?? []).filter((t) => t.id !== appliedTagId) } },
        });
      }
      return undefined;
    }

    apiClient.chat
      .searchTags({ context: pageContext, query: segmentName })
      .then((res) => {
        if (cancelled) return;

        // The segment's group tag is the one linked to this segment identifier.
        const match = res.data.results.find((t) => t.metadata?.segmentIdentifier === segmentId);
        if (!match) return;

        const previousTagId = appliedTagIdRef.current;
        appliedTagIdRef.current = match.id;

        const others = (tagsRef.current ?? []).filter((t) => t.id !== match.id && t.id !== previousTagId);
        dispatch({
          type: 'filters/updated',
          payload: {
            filters: {
              tags: [
                ...others,
                {
                  id: match.id,
                  name: match.name,
                  metadata: { canRemove: false, segmentIdentifier: segmentId },
                },
              ],
            },
          },
        });
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
    // `tags` is read through a ref so applying the tag does not retrigger this effect.
  }, [apiClient, chatIdentifier, dispatch, pageContext, segmentId, segmentName]);

  // Same for a Patient Journey: the journey page puts `metadata.patientJourneyId` on the page
  // context; show that journey as a locked entry in Tags (journeys come back from the tag search
  // for a project context, under a synthetic negative id with `metadata.journeyId`).
  const journeyMetadataId = pageContext ? getChatContextMetadata(pageContext)?.patientJourneyId : undefined;
  const journeyId = typeof journeyMetadataId === 'number' && journeyMetadataId > 0 ? journeyMetadataId : undefined;
  const appliedJourneyTagIdRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!pageContext || journeyId === undefined) {
      const appliedTagId = appliedJourneyTagIdRef.current;
      if (appliedTagId !== null) {
        appliedJourneyTagIdRef.current = null;
        dispatch({
          type: 'filters/updated',
          payload: { filters: { tags: (tagsRef.current ?? []).filter((t) => t.id !== appliedTagId) } },
        });
      }
      return undefined;
    }

    apiClient.chat
      .searchTags({ context: pageContext, query: '' })
      .then((res) => {
        if (cancelled) return;

        const match = res.data.results.find((t) => t.metadata?.journeyId === journeyId);
        if (!match) return;

        const previousTagId = appliedJourneyTagIdRef.current;
        appliedJourneyTagIdRef.current = match.id;

        const others = (tagsRef.current ?? []).filter((t) => t.id !== match.id && t.id !== previousTagId);
        dispatch({
          type: 'filters/updated',
          payload: {
            filters: {
              tags: [
                ...others,
                { id: match.id, name: match.name, metadata: { canRemove: false, journeyId } },
              ],
            },
          },
        });
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
    // `tags` is read through a ref so applying the tag does not retrigger this effect.
  }, [apiClient, chatIdentifier, dispatch, journeyId, pageContext]);
}
